/**
 * Scroll helpers for the skill description panel (path URL + fragment).
 */

/**
 * Browsers scroll the document for same-document URL fragments. This app uses a fixed shell
 * (overflow hidden) and scrolls headings inside the skill panel instead; undo any window/html/body
 * scroll so the tree and panel stay aligned with the viewport.
 */
export function resetWindowAndRootScroll(): void {
    if (typeof window === 'undefined') {
        return;
    }
    window.scrollTo(0, 0);
    const doc = document.documentElement;
    doc.scrollTop = 0;
    doc.scrollLeft = 0;
    document.body.scrollTop = 0;
    document.body.scrollLeft = 0;
}

/** Run once now and once after paint; browser fragment scrolling may run after our first reset. */
export function undoBrowsersDocumentFragmentScroll(): void {
    resetWindowAndRootScroll();
    requestAnimationFrame(() => resetWindowAndRootScroll());
}

/** Inputs that decide whether the skill panel can scroll to a URL fragment (shared by layout + hashchange paths). */
export type SkillPanelFragmentScrollReadiness = {
    isCollapsed: boolean;
    loading: boolean;
    hasSkill: boolean;
    isEditing: boolean;
    scrollParent: HTMLElement | null;
    /**
     * Whether this viewer owns the document's scroll position. True in the hosted app,
     * whose shell is the document. False when embedded in someone else's page: the
     * browser never scrolled the document for a fragment the memory router handled, so
     * there is nothing to undo, and resetting would yank the host page to the top.
     */
    ownsDocumentScroll: boolean;
};

export function canScrollSkillPanelToFragment(readiness: SkillPanelFragmentScrollReadiness): boolean {
    return !(
        readiness.isCollapsed ||
        readiness.loading ||
        !readiness.hasSkill ||
        readiness.isEditing ||
        !readiness.scrollParent
    );
}

/** Build readiness from panel state + scroll container ref (read `scrollParent` at call time). */
export function buildSkillPanelFragmentScrollReadiness(input: {
    isCollapsed: boolean;
    loading: boolean;
    hasSkill: boolean;
    isEditing: boolean;
    scrollParent: HTMLElement | null;
    ownsDocumentScroll?: boolean;
}): SkillPanelFragmentScrollReadiness {
    return {
        isCollapsed: input.isCollapsed,
        loading: input.loading,
        hasSkill: input.hasSkill,
        isEditing: input.isEditing,
        scrollParent: input.scrollParent,
        ownsDocumentScroll: input.ownsDocumentScroll ?? true,
    };
}

/**
 * Scroll using the current `window.location.hash` (same-document navigation). Used by the
 * hashchange listener; testable by spying `tryScrollSkillPanelToFragment`.
 */
export function scrollSkillPanelToWindowLocationHash(
    getReadiness: () => SkillPanelFragmentScrollReadiness
): void {
    if (typeof window === 'undefined') {
        return;
    }
    tryScrollSkillPanelToFragment(window.location.hash, getReadiness());
}

/**
 * True when a transitionend event is the skill panel's own width transition settling
 * (expand/collapse finished). Child transitions bubble to the panel too; ignore those.
 */
export function isSkillPanelWidthTransitionEnd(event: {
    target: unknown;
    currentTarget: unknown;
    propertyName: string;
}): boolean {
    return event.target === event.currentTarget && event.propertyName === 'width';
}

export function registerSkillPanelHashChangeScroll(
    getReadiness: () => SkillPanelFragmentScrollReadiness
): () => void {
    if (typeof window === 'undefined') {
        return () => {};
    }
    const onHashChange = () => scrollSkillPanelToWindowLocationHash(getReadiness);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
}

/**
 * Parse `hash`, then scroll the panel scroll parent to the target id, or reset window scroll when
 * the panel cannot take over (collapsed, loading, etc.). Used from both useLayoutEffect (React
 * `location.hash` / skill HTML updates) and a `hashchange` listener (same-document fragment
 * changes that still need panel-local scrolling).
 */
export function tryScrollSkillPanelToFragment(
    hash: string,
    readiness: SkillPanelFragmentScrollReadiness
): void {
    const targetId = parseUrlFragmentToTargetId(hash);
    if (!targetId) {
        return;
    }
    if (!canScrollSkillPanelToFragment(readiness)) {
        if (readiness.ownsDocumentScroll) undoBrowsersDocumentFragmentScroll();
        return;
    }
    const scrollParent = readiness.scrollParent;
    if (!scrollParent) {
        return;
    }
    scrollScrollParentToId(scrollParent, targetId, readiness.ownsDocumentScroll);
}

export function parseUrlFragmentToTargetId(hash: string): string | null {
    if (!hash || hash === '#') {
        return null;
    }
    const raw = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!raw) {
        return null;
    }
    try {
        return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch {
        return raw;
    }
}

function queryByFragmentId(scrollParent: HTMLElement, targetId: string): Element | null {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        try {
            return scrollParent.querySelector(`#${CSS.escape(targetId)}`);
        } catch {
            return null;
        }
    }
    const escaped = targetId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    try {
        return scrollParent.querySelector(`[id="${escaped}"]`);
    } catch {
        return null;
    }
}

/**
 * Scrolls `scrollParent` so the element with id `targetId` (inside it) is at the top.
 * Returns true if an element was found and scrolled.
 */
export function scrollScrollParentToId(
    scrollParent: HTMLElement,
    targetId: string,
    ownsDocumentScroll: boolean = true,
): boolean {
    if (!targetId) {
        return false;
    }
    const target = queryByFragmentId(scrollParent, targetId);
    if (!(target instanceof HTMLElement)) {
        if (ownsDocumentScroll) undoBrowsersDocumentFragmentScroll();
        return false;
    }
    const parentRect = scrollParent.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = targetRect.top - parentRect.top + scrollParent.scrollTop;
    scrollParent.scrollTo({ top: nextTop, behavior: 'auto' });
    if (ownsDocumentScroll) undoBrowsersDocumentFragmentScroll();
    return true;
}
